import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { OrderType } from '~/constants/SOAPStates'
import {
  clearCurrentUnifiedOrder,
  fetchUnifiedOrder,
} from '~/store/actions/orders'
import {
  fetchPrescriptionWithRefills,
  getPrescriptionById,
  getPrescriptionError,
} from '~/store/duck/prescriptions'
import { getOrdersError, getUnifiedOrder } from '~/store/reducers/orders'

import {
  convertPrescriptionToRxCardItem,
  orderToRxCardItem,
} from './timeline/prescriptions/rxCardItem'

export type DetailsMode = 'orderedDrug' | 'prescription'

export const isDrugSubtype = (subtype: string | null): boolean =>
  subtype != null && subtype.toLowerCase() === 'drug'

export function usePrescriptionDetailsContent(
  prescriptionId: string | undefined,
  mode: DetailsMode,
) {
  const dispatch = useDispatch()

  const unifiedOrder = useSelector(getUnifiedOrder)
  const ordersError = useSelector(getOrdersError)

  const prescription = useSelector(
    prescriptionId ? getPrescriptionById(prescriptionId) : () => undefined,
  )
  const prescriptionError = useSelector(getPrescriptionError)

  useEffect(() => {
    if (!prescriptionId) return

    if (mode === 'orderedDrug') {
      if (unifiedOrder?.id === prescriptionId) return
      if (unifiedOrder?.id && unifiedOrder.id !== prescriptionId) {
        dispatch(clearCurrentUnifiedOrder())
      }
      dispatch(fetchUnifiedOrder(prescriptionId, OrderType.INVENTORY))
    } else {
      if (prescription?.id === prescriptionId) return
      dispatch(fetchPrescriptionWithRefills(prescriptionId))
    }
    // Guards read current store state; effect only re-runs on prescriptionId/mode change
  }, [prescriptionId, mode, dispatch])

  useEffect(
    () => () => {
      dispatch(clearCurrentUnifiedOrder())
    },
    [dispatch],
  )

  const { clientId, patientId } = useMemo(() => {
    if (mode === 'orderedDrug') {
      const lineItem = unifiedOrder?.lineItem as
        | { clientId?: string; patientId?: string }
        | undefined

      return {
        clientId: lineItem?.clientId ?? unifiedOrder?.clientId ?? null,
        patientId: lineItem?.patientId ?? unifiedOrder?.patientId ?? null,
      }
    }

    return {
      clientId: prescription?.client?.id ?? null,
      patientId: prescription?.patient?.id ?? null,
    }
  }, [mode, unifiedOrder, prescription])

  const item = useMemo(() => {
    if (mode === 'orderedDrug') {
      return unifiedOrder
        ? orderToRxCardItem(unifiedOrder, {
            clientId: clientId ?? undefined,
            patientId: patientId ?? undefined,
          })
        : undefined
    }
    return prescription
      ? convertPrescriptionToRxCardItem(prescription)
      : undefined
  }, [mode, unifiedOrder, prescription, clientId, patientId])

  const isLoading =
    mode === 'orderedDrug'
      ? Boolean(
          prescriptionId && unifiedOrder?.id !== prescriptionId && !ordersError,
        )
      : Boolean(prescriptionId && !prescription && !prescriptionError)

  const hasData =
    mode === 'orderedDrug'
      ? Boolean(prescriptionId && unifiedOrder?.id === prescriptionId)
      : Boolean(prescriptionId && prescription?.id === prescriptionId)

  return {
    item,
    clientId,
    patientId,
    isLoading,
    hasData,
  }
}
